import React, { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Drawer } from 'antd';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import FadeIn from '../components/FadeIn';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import DashboardRoutes from '../routes/DashboardRoutes';
import { CompanyOperationalStatus, SIDEBAR_WIDTH } from '../services/exports/Constants';
import OnboardingGuide from '../components/onboarding/OnboardingGuide';
import OwnerOnboardingEntry from '../components/onboarding/OwnerOnboardingEntry';
import usePermissions, { ManagerRole } from '../hooks/auth/usePermissions';
import useScreenType from '../hooks/context/useScreenType';
import EnvironmentBanner from '../components/layout/EnvironmentBanner';
import useAppState from '../hooks/context/useAppState';
import CompanySuspendedBanner from '../components/layout/CompanySuspendedBanner';
import PaymentGatewayKycBanner from '../components/layout/PaymentGatewayKycBanner';
import DeliveryPaymentBlockBanner from '../components/layout/DeliveryPaymentBlockBanner';
import StripeProcessingFeeBanner from '../components/layout/StripeProcessingFeeBanner';
import PartnerPayoutAccountBanner from '../components/layout/PartnerPayoutAccountBanner';
import NoFranchiseBanner from '../components/layout/NoFranchiseBanner';
import { ScreenEnum } from '../hooks/navigation/Screens';
import { isPlgOnboardingProvider } from '../services/tenant/plgOnboarding';

const loading = (
  <div className="pt-4 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

// The partner onboarding page manages its own scroll and sticky footer, so it
// renders full-bleed (no page padding) inside the content area. It is otherwise
// an ordinary dashboard page: the normal sidebar stays, and its phase nav lives
// inside the page itself rather than replacing the app chrome.
// The onboarding page manages its own scroll and sticky footer, so it renders
// full-bleed (no page padding). Every other page — the standalone
// "New restaurant" page included — keeps the normal dashboard padding.
const FULL_BLEED_ROUTES = [ScreenEnum.RestaurantOnboarding, ScreenEnum.NewRestaurant];

function Layout() {
  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { isMerchant, isSuperAdmin, isMasterProvider, canAccessScreen, hasAnyRole } = usePermissions();
  const { showSideBar, setShowSideBar } = useAppState();
  const { isDesktop } = useScreenType();

  const applyMiddleware = (route) => {
    if (!route.middleware) {
      return <route.component />;
    }

    if (Array.isArray(route.middleware)) {
      return collect(route.middleware)
        .reverse()
        .reduce((carry, Middleware) => <Middleware>{carry}</Middleware>, <route.component />);
    }

    return (
      <route.middleware>
        <route.component />
      </route.middleware>
    );
  };

  function renderRoutes() {
    return (
      <>
        <Routes>
          {DashboardRoutes.filter((route) => route.component && !route.excluded && canAccessScreen(route.path)).map(
            (route, idx) => (
              <Route
                key={idx}
                // Routes match exactly by default; v5 prefix routes (no `exact`)
                // need a trailing `/*` to keep matching their sub-paths.
                path={route.exact ? route.path : `${route.path.replace(/\/$/, '')}/*`}
                element={
                  // Per-route Suspense keyed by path: React Router v7 wraps navigation
                  // in a transition, which would keep the old page visible while the
                  // next lazy chunk loads. Keying the boundary remounts it on each
                  // navigation so the fallback shows immediately (matching the old v5
                  // behaviour) instead of silently waiting.
                  <Suspense key={route.path} fallback={loading}>
                    {FULL_BLEED_ROUTES.includes(route.path) ? (
                      // Owns its own scroll + sticky footer, so no page padding.
                      <div className="flex-1 min-h-0">{applyMiddleware(route)}</div>
                    ) : (
                      <FadeIn className="flex-1 min-h-0 overflow-y-scroll scrollbar-hide lg:p-xl p-md">
                        {applyMiddleware(route)}
                      </FadeIn>
                    )}
                  </Suspense>
                }
              />
            ),
          )}
          <Route
            path="*"
            element={
              <Navigate
                // Customer base is FoodAmigos sales tooling: only master-provider
                // sales staff land there. Partners (any non-master provider, PLG or
                // not) and store users land on the Dashboard.
                to={
                  isMerchant || isSuperAdmin || !isMasterProvider || isPlgOnboardingProvider(serviceProvider)
                    ? ScreenEnum.Dashboard
                    : ScreenEnum.CustomerBase
                }
                replace
              />
            }
          />
        </Routes>
        {/* The go-live checklist guide is FoodAmigos' onboarding flow; self-serve (PLG) partners get the wizard instead. */}
        {!hasAnyRole([ManagerRole.StoreAdmin, ManagerRole.StoreEmployee, ManagerRole.StoreOperator]) &&
          !isPlgOnboardingProvider(serviceProvider) && <OnboardingGuide />}
      </>
    );
  }

  const renderSidebarDrawer = useMemo(
    () => (
      <Drawer
        open={showSideBar}
        placement="left"
        styles={{
          body: {
            padding: '0px',
            overflow: 'hidden',
            height: '100%',
            overscrollBehaviorX: 'none',
          },
          header: { display: 'none' },
        }}
        onClose={() => setShowSideBar(false)}
        size={window.innerWidth * 0.8}
      >
        <Sidebar />
      </Drawer>
    ),
    [showSideBar],
  );

  if (!isDesktop) {
    return (
      <>
        <div className="w-full fixed inset-0 flex flex-col overflow-hidden">
          <div className="flex-shrink-0">
            <EnvironmentBanner />
            <Header />
            {company?.operational_status === CompanyOperationalStatus.Suspended && <CompanySuspendedBanner />}
            <PaymentGatewayKycBanner />
            <StripeProcessingFeeBanner />
            <DeliveryPaymentBlockBanner />
            <PartnerPayoutAccountBanner />
            <NoFranchiseBanner />
            <OwnerOnboardingEntry />
          </div>
          {renderRoutes()}
        </div>
        {renderSidebarDrawer}
      </>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background-inkWhite-white_1">
      <div
        className={classnames(
          'fixed left-0 top-0 h-screen scrollbar-hide ease-in-out duration-300 border-[1px] border-brand-inkGrey-grey_2 border-solid border-t-none border-b-none border-l-none',
          {
            'translate-x-0': showSideBar,
            '-translate-x-full': !showSideBar,
          },
        )}
        style={{
          width: SIDEBAR_WIDTH,
        }}
      >
        <Sidebar />
      </div>
      <div
        className={classnames('relative w-full min-w-0 h-screen flex flex-col ease-in-out duration-300', {
          'ml-[350px]': showSideBar,
        })}
      >
        <div className="flex-shrink-0 z-50" id="header-container">
          <EnvironmentBanner />
          <Header />
          {company?.operational_status === CompanyOperationalStatus.Suspended && <CompanySuspendedBanner />}
          <PaymentGatewayKycBanner />
          <StripeProcessingFeeBanner />
          <DeliveryPaymentBlockBanner />
          <PartnerPayoutAccountBanner />
          <NoFranchiseBanner />
          <OwnerOnboardingEntry />
        </div>
        {renderRoutes()}
      </div>
    </div>
  );
}

export default Layout;
